/**
 * Renders the small markdown subset release notes are written in, as React elements.
 *
 * Supported: `#`–`######` headings (the first two levels as <h4>, deeper as <h5>, since the
 * drawer already shows the note's title), paragraphs, `-`/`*` and `1.` lists, `**bold**`,
 * `` `code` `` and `[text](https://…)` / `mailto:` links. Anything else is shown as written.
 *
 * Nothing goes through innerHTML, so HTML in a note is text, and a link whose scheme is not
 * http(s) or mailto stays as its source text. The output carries no classes: each app styles
 * it under its own `.release-markdown` wrapper.
 *
 * The back-office preview uses a copy of this file (wade-backoffice
 * src/utils/releaseMarkdown.tsx); keep the two identical so authors see what customers see.
 */
import React from "react";

type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; lines: string[] }
  | { kind: "list"; ordered: boolean; items: string[] };

const HEADING = /^(#{1,6})\s+(.+?)\s*#*\s*$/;
const UNORDERED_ITEM = /^\s*[-*+]\s+(.*)$/;
const ORDERED_ITEM = /^\s*\d+[.)]\s+(.*)$/;
// Bold needs non-space just inside both markers, so "2 ** 3" stays as written.
const INLINE = /\*\*(\S(?:[^*]*\S)?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;
const SAFE_URL = /^(https?:\/\/|mailto:)/i;

function parseBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  let open: Block | null = null;

  for (const line of markdown.replace(/\r\n?/g, "\n").split("\n")) {
    if (!line.trim()) {
      open = null;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1].length, text: heading[2] });
      open = null;
      continue;
    }
    const unordered = UNORDERED_ITEM.exec(line);
    const ordered = unordered ? null : ORDERED_ITEM.exec(line);
    const item = unordered || ordered;
    if (item) {
      const isOrdered = Boolean(ordered);
      if (open?.kind !== "list" || open.ordered !== isOrdered) {
        open = { kind: "list", ordered: isOrdered, items: [] };
        blocks.push(open);
      }
      open.items.push(item[1].trim());
      continue;
    }
    if (open?.kind === "list") {
      // A wrapped line continues the item above it.
      open.items[open.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    if (open?.kind !== "paragraph") {
      open = { kind: "paragraph", lines: [] };
      blocks.push(open);
    }
    open.lines.push(line.trim());
  }

  // A blank line between items still makes one list.
  return blocks.reduce<Block[]>((merged, block) => {
    const previous = merged[merged.length - 1];
    if (
      block.kind === "list" &&
      previous?.kind === "list" &&
      previous.ordered === block.ordered
    ) {
      previous.items.push(...block.items);
    } else {
      merged.push(block);
    }
    return merged;
  }, []);
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  INLINE.lastIndex = 0;

  for (let match = INLINE.exec(text); match; match = INLINE.exec(text)) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${match.index}`;
    const [source, bold, code, linkText, href] = match;
    if (bold !== undefined) {
      nodes.push(<strong key={key}>{bold}</strong>);
    } else if (code !== undefined) {
      nodes.push(<code key={key}>{code}</code>);
    } else if (SAFE_URL.test(href)) {
      nodes.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {linkText}
        </a>
      );
    } else {
      nodes.push(source);
    }
    last = match.index + source.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function renderReleaseMarkdown(markdown: string): React.ReactNode[] {
  return parseBlocks(markdown || "").map((block, index) => {
    const key = `b${index}`;
    switch (block.kind) {
      case "heading":
        return block.level <= 2 ? (
          <h4 key={key}>{renderInline(block.text, key)}</h4>
        ) : (
          <h5 key={key}>{renderInline(block.text, key)}</h5>
        );
      case "paragraph":
        return <p key={key}>{renderInline(block.lines.join(" "), key)}</p>;
      case "list": {
        const items = block.items.map((item, i) => (
          <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
        ));
        return block.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>;
      }
      default:
        return null;
    }
  });
}
